import { Select, SelectItem } from '@heroui/react';
import _ from 'lodash';
import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFetchAccountSpecialtiesQuery } from '@/slices/specialties-api-slice.ts';
import { useFetchStatesQuery } from '@/slices/state-api-slice.ts';
import type { StateModel } from '../../types/address.type.ts';
import type { OrganizationSearchParams } from '../../types/organization-search-params.type.ts';
import { keywordDefinition } from '../../types/search-form.type.ts';
import SearchForm from '../search-form.tsx';
import { StateSelect } from '../state-select.tsx';

const OrganizationFilterForm: FC = () => {
  const { control } = useFormContext<OrganizationSearchParams>();
  const { data: specialties, isLoading } = useFetchAccountSpecialtiesQuery();

  return (
    <div className='flex flex-col gap-4'>
      <Controller
        name='specialty'
        control={control}
        render={({ field, fieldState }) => (
          <Select
            label='Specialty'
            placeholder='Select specialty'
            variant='bordered'
            isLoading={isLoading}
            isInvalid={fieldState.invalid}
            errorMessage={fieldState.error?.message}
            selectedKeys={field.value ? [field.value] : []}
            onSelectionChange={keys => {
              const selectedKey = Array.from(keys)[0];
              field.onChange(selectedKey || '');
            }}
          >
            {specialties?.map(specialty => (
              <SelectItem key={specialty.description}>{specialty.description}</SelectItem>
            )) || []}
          </Select>
        )}
      />
      <StateSelect name='state' />
    </div>
  );
};

const OrganizationSearchForm: FC = () => {
  const { data: states } = useFetchStatesQuery();

  const getSearchModel = (value: URLSearchParams) => {
    const state = states?.find(x => x.id === parseInt(value.get('state')));

    return {
      keyword: value.get('keyword'),
      specialty: value.get('specialty'),
      state: value.has('state') ? state : null,
    };
  };

  const definitions = [
    keywordDefinition,
    {
      key: 'specialty',
      label: 'Specialty',
      displayFunc: (value: string) => value,
    },
    {
      key: 'state',
      label: 'State',
      displayFunc: (value: StateModel) => value?.abbreviation,
    },
  ];

  const getUrlSearchParams = (value: OrganizationSearchParams) => {
    const params = new URLSearchParams();

    if (value?.state?.id || value?.keyword || value?.specialty) {
      if (value.state?.id && _.isNumber(parseInt(value.state.id.toString()))) {
        params.set('state', value.state.id.toString());
      }

      if (value.keyword) {
        params.set('keyword', value.keyword.trim());
      }

      if (value.specialty) {
        params.set('specialty', value.specialty);
      }

      return params;
    }
  };

  const defaultValue = {
    keyword: '',
    specialty: '',
    state: { id: '' },
  };

  return (
    <SearchForm<OrganizationSearchParams>
      defaultValue={defaultValue}
      getUrlSearchParams={getUrlSearchParams}
      getSearchParamModel={getSearchModel}
      searchCriteriaDisplayDefinitions={definitions}
      filterForm={<OrganizationFilterForm />}
    />
  );
};

export default OrganizationSearchForm;
